import { create } from "zustand";
import { persist } from "zustand/middleware";
import { calculateStars, getMission, type MissionId } from "../game/missions";

export type Phase = "menu" | "briefing" | "playing" | "paused" | "complete";

type Result = { stars: number; bestTime: number };
type GameEvent = { id: number; text: string };

type GameState = {
  phase: Phase;
  selected: MissionId;
  completed: number;
  total: number;
  elapsed: number;
  speed: number;
  score: number;
  stars: number;
  event: GameEvent | null;
  results: Partial<Record<MissionId, Result>>;
  virtualThrottle: number;
  virtualSteer: number;
  selectMission: (id: MissionId) => void;
  showBriefing: (id?: MissionId) => void;
  startMission: () => void;
  pause: () => void;
  resume: () => void;
  quit: () => void;
  setProgress: (completed: number) => void;
  setTelemetry: (speed: number, elapsed: number) => void;
  notify: (text: string) => void;
  finishMission: () => void;
  setVirtualInput: (throttle: number, steer: number) => void;
};

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      phase: "menu",
      selected: "mow",
      completed: 0,
      total: getMission("mow").total,
      elapsed: 0,
      speed: 0,
      score: 0,
      stars: 0,
      event: null,
      results: {},
      virtualThrottle: 0,
      virtualSteer: 0,
      selectMission: (selected) => set({ selected }),
      showBriefing: (id) => {
        const selected = id ?? get().selected;
        set({ phase: "briefing", selected });
      },
      startMission: () => {
        const mission = getMission(get().selected);
        set({
          phase: "playing",
          completed: 0,
          total: mission.total,
          elapsed: 0,
          speed: 0,
          score: 0,
          stars: 0,
          event: null,
          virtualThrottle: 0,
          virtualSteer: 0,
        });
      },
      pause: () =>
        set({ phase: "paused", virtualThrottle: 0, virtualSteer: 0 }),
      resume: () => set({ phase: "playing" }),
      quit: () =>
        set({ phase: "menu", speed: 0, virtualThrottle: 0, virtualSteer: 0 }),
      setProgress: (completed) => set({ completed }),
      setTelemetry: (speed, elapsed) => set({ speed, elapsed }),
      notify: (text) => set({ event: { id: Date.now(), text } }),
      finishMission: () => {
        const state = get();
        if (state.phase !== "playing") return;
        const mission = getMission(state.selected);
        const stars = calculateStars(state.elapsed, mission.parTime);
        const score = Math.max(
          100,
          Math.round(1000 * stars + 1000 - state.elapsed * 4),
        );
        const previous = state.results[state.selected];
        const result = {
          stars: Math.max(previous?.stars ?? 0, stars),
          bestTime: Math.min(
            previous?.bestTime ?? Number.POSITIVE_INFINITY,
            state.elapsed,
          ),
        };
        set({
          phase: "complete",
          stars,
          score,
          speed: 0,
          results: { ...state.results, [state.selected]: result },
        });
      },
      setVirtualInput: (virtualThrottle, virtualSteer) =>
        set({ virtualThrottle, virtualSteer }),
    }),
    {
      name: "rover-works-progress-v1",
      partialize: (state) => ({
        selected: state.selected,
        results: state.results,
      }),
    },
  ),
);
